/**
 * Webhook Debugging Utility
 *
 * This utility helps debug webhook issues by providing:
 * 1. Easy access to webhook logs
 * 2. Manual webhook testing
 * 3. Context inspection
 */

export interface WebhookDebugInfo {
  timestamp: string;
  event: string;
  context: any;
  payload: any;
  success: boolean;
  error?: any;
}

/**
 * Get all webhook debug logs from sessionStorage
 */
export function getWebhookDebugLogs(): any[] {
  try {
    return JSON.parse(sessionStorage.getItem('webhookLogs') || '[]');
  } catch {
    return [];
  }
}

/**
 * Clear all webhook debug logs
 */
export function clearWebhookDebugLogs(): void {
  sessionStorage.removeItem('webhookLogs');
  console.log('[WebhookDebugger] Cleared all webhook logs');
}

/**
 * Print formatted webhook logs to console
 */
export function printWebhookDebugLogs(): void {
  const logs = getWebhookDebugLogs();

  // Filter for webhook-related logs only
  const webhookLogs = logs.filter(
    (log) =>
      log.message &&
      (log.message.includes('sendWebhook') ||
        log.message.includes('webhook') ||
        log.message.includes('Webhook'))
  );

  console.log('=== WEBHOOK DEBUG LOGS ===');
  console.log(
    `Total logs: ${logs.length}, Webhook-related: ${webhookLogs.length}`
  );

  webhookLogs.forEach((log, index) => {
    console.log(
      `\n${index + 1}. [${log.timestamp}] ${log.message || 'No message'}`
    );
    if (log.data) {
      console.log('   Data:', log.data);
    }
  });

  console.log('=== END WEBHOOK LOGS ===');
}

/**
 * Print all logs (not just webhook-related)
 */
export function printAllDebugLogs(): void {
  const logs = getWebhookDebugLogs();
  console.log('=== ALL DEBUG LOGS ===');
  console.log(`Total logs: ${logs.length}`);

  logs.forEach((log, index) => {
    console.log(
      `\n${index + 1}. [${log.timestamp}] ${log.message || 'No message'}`
    );
    if (log.data) {
      console.log('   Data:', log.data);
    }
  });

  console.log('=== END ALL LOGS ===');
}

/**
 * Test abandoned endpoint functionality manually
 */
export async function testAbandonedEndpoint(
  event:
    | 'TERMS_REJECTED'
    | 'TSP_SELECTION_QUIT'
    | 'TSP_NOT_FOUND'
    | 'ERROR_QUIT',
  overrideContext?: any
): Promise<void> {
  console.log('[WebhookDebugger] Testing abandoned endpoint manually...');

  try {
    // Get token from various sources (same logic as analytics.ts)
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    const localToken = localStorage.getItem('catena_link_token');
    const actualToken = urlToken || localToken || 'test-token';

    console.log('[WebhookDebugger] Token sources:', {
      urlToken: urlToken ? `${urlToken.substring(0, 20)}...` : 'null',
      localToken: localToken ? `${localToken.substring(0, 20)}...` : 'null',
      actualToken: actualToken ? `${actualToken.substring(0, 20)}...` : 'null',
    });

    // Create a more realistic JWT state based on current context
    const jwt = {
      exists: !!actualToken,
      isValid: !!actualToken,
      isExpired: false,
      isDecodable: true,
      rawToken: actualToken,
      decodedPayload: null,
      serviceId: 3, // This should match your test service
      fleetCode: 'Test3', // This should match your test fleet
      redirectURISuccess:
        'https://webhook.site/bfb3c726-a94b-4c47-89ed-546c92df895a',
      redirectURIError:
        'https://webhook.site/bfb3c726-a94b-4c47-89ed-546c92df895a',
      errorType: null,
      errorMessage: null,
      canRedirectToService: true,
      preferredRedirectURI:
        'https://webhook.site/bfb3c726-a94b-4c47-89ed-546c92df895a',
      theme: null,
    };

    // Create analytics context
    const { createAnalyticsContext } = await import('./analytics');
    const context = createAnalyticsContext(jwt as any, 'Test Service', {
      redirectURISuccess:
        'https://webhook.site/bfb3c726-a94b-4c47-89ed-546c92df895a',
      redirectURIError:
        'https://webhook.site/bfb3c726-a94b-4c47-89ed-546c92df895a',
      tsp: 'test-tsp',
      ...overrideContext,
    });

    console.log('[WebhookDebugger] Context created:', context);

    // Import and call the analytics functions
    const { Analytics } = await import('./analytics');

    switch (event) {
      case 'TERMS_REJECTED':
        await Analytics.confirmedSkip.termsRejected(context);
        break;
      case 'TSP_SELECTION_QUIT':
        await Analytics.confirmedSkip.tspSelectionQuit(context);
        break;
      case 'TSP_NOT_FOUND':
        await Analytics.confirmedSkip.tspNotFound(context);
        break;
      case 'ERROR_QUIT':
        await Analytics.confirmedSkip.errorQuit(
          context,
          'manual_test',
          'WebhookDebugger'
        );
        break;
    }

    console.log('[WebhookDebugger] Abandoned endpoint test completed');
  } catch (error) {
    console.error('[WebhookDebugger] Abandoned endpoint test failed:', error);
  }
}

/**
 * Inspect current analytics context
 */
export function inspectCurrentContext(): void {
  console.log('[WebhookDebugger] Inspecting current context...');

  try {
    // Get token from various sources
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    const localToken = localStorage.getItem('catena_link_token');

    console.log('Token sources:', {
      urlToken: urlToken ? `${urlToken.substring(0, 20)}...` : 'null',
      localToken: localToken ? `${localToken.substring(0, 20)}...` : 'null',
    });

    // Check if JWT hook is available
    console.log('Current URL:', window.location.href);
    console.log('URL params:', Object.fromEntries(urlParams));

    // Try to get service name from DOM or context
    const titleElement = document.querySelector('h5');
    const serviceName =
      titleElement?.textContent?.match(/^(.+?) uses/)?.[1] || 'Unknown Service';
    console.log('Detected service name:', serviceName);
  } catch (error) {
    console.error('[WebhookDebugger] Context inspection failed:', error);
  }
}

/**
 * Check what the webhook payload would look like with current context
 */
export async function inspectWebhookPayload(): Promise<void> {
  console.log('[WebhookDebugger] Inspecting webhook payload...');

  try {
    const { createAnalyticsContext } = await import('./analytics');

    // Get current context
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    const localToken = localStorage.getItem('catena_link_token');
    const actualToken = urlToken || localToken;

    // Create JWT-like object
    const jwt = {
      exists: !!actualToken,
      isValid: !!actualToken,
      isExpired: false,
      isDecodable: true,
      rawToken: actualToken,
      decodedPayload: null,
      serviceId: 3,
      fleetCode: 'Test3',
      redirectURISuccess:
        'https://webhook.site/bfb3c726-a94b-4c47-89ed-546c92df895a',
      redirectURIError:
        'https://webhook.site/bfb3c726-a94b-4c47-89ed-546c92df895a',
      errorType: null,
      errorMessage: null,
      canRedirectToService: true,
      preferredRedirectURI:
        'https://webhook.site/bfb3c726-a94b-4c47-89ed-546c92df895a',
      theme: null,
    };

    // Get service name from DOM
    const titleElement = document.querySelector('h5');
    const serviceName =
      titleElement?.textContent?.match(/^(.+?) uses/)?.[1] || 'Test Service';

    const context = createAnalyticsContext(jwt as any, serviceName, {
      redirectURISuccess:
        'https://webhook.site/bfb3c726-a94b-4c47-89ed-546c92df895a',
      redirectURIError:
        'https://webhook.site/bfb3c726-a94b-4c47-89ed-546c92df895a',
    });

    console.log('[WebhookDebugger] Analytics context:', context);

    // Simulate webhook payload creation
    const payload: any = {
      fleet_code: context.fleetCode || 'unknown',
      link_token: context.token || 'unknown',
      status_code: 'TSP_ONBOARDING_NOT_STARTED',
      time: new Date().toISOString(),
      tsp_source: context.tsp || 'unknown',
    };

    if (context.serviceId) {
      payload.fleet_id = context.serviceId;
    }

    payload.error =
      'Fleet onboard could not be completed. User exited the onboarding flow.';

    console.log('[WebhookDebugger] Webhook payload would be:', payload);

    // Check for potential issues
    const issues = [];
    if (!context.token) issues.push('No token available');
    if (!context.fleetCode) issues.push('No fleet code');
    if (!context.serviceId) issues.push('No service ID');

    if (issues.length > 0) {
      console.warn('[WebhookDebugger] Potential issues:', issues);
    } else {
      console.log('[WebhookDebugger] Payload looks good!');
    }
  } catch (error) {
    console.error('[WebhookDebugger] Payload inspection failed:', error);
  }
}

/**
 * Global debugging interface
 */
export const WebhookDebugger = {
  getLogs: getWebhookDebugLogs,
  clearLogs: clearWebhookDebugLogs,
  printLogs: printWebhookDebugLogs,
  printAllLogs: printAllDebugLogs,
  testAbandonedEndpoint,
  inspectContext: inspectCurrentContext,
  inspectPayload: inspectWebhookPayload,

  // Quick test functions for abandoned endpoint
  testTermsRejected: () => testAbandonedEndpoint('TERMS_REJECTED'),
  testTspSelectionQuit: () => testAbandonedEndpoint('TSP_SELECTION_QUIT'),
  testTspNotFound: () => testAbandonedEndpoint('TSP_NOT_FOUND'),
  testErrorQuit: () => testAbandonedEndpoint('ERROR_QUIT'),

  // Test specific scenarios with current page context
  testSelectTSPQuit: async () => {
    console.log(
      '[WebhookDebugger] Testing SelectTSP quit scenario with current context...'
    );
    try {
      const { Analytics, createAnalyticsContext } = await import('./analytics');

      // Get current context
      const urlParams = new URLSearchParams(window.location.search);
      const urlToken = urlParams.get('token');
      const localToken = localStorage.getItem('catena_link_token');
      const actualToken = urlToken || localToken;

      if (!actualToken) {
        console.warn(
          '[WebhookDebugger] No token found for SelectTSP quit test'
        );
        return;
      }

      // Create JWT-like object
      const jwt = {
        exists: true,
        isValid: true,
        isExpired: false,
        isDecodable: true,
        rawToken: actualToken,
        decodedPayload: null,
        serviceId: 3,
        fleetCode: 'Test3',
        redirectURISuccess:
          'https://webhook.site/bfb3c726-a94b-4c47-89ed-546c92df895a',
        redirectURIError:
          'https://webhook.site/bfb3c726-a94b-4c47-89ed-546c92df895a',
        errorType: null,
        errorMessage: null,
        canRedirectToService: true,
        preferredRedirectURI:
          'https://webhook.site/bfb3c726-a94b-4c47-89ed-546c92df895a',
        theme: null,
      };

      // Get service name from DOM
      const titleElement = document.querySelector('h5');
      const serviceName =
        titleElement?.textContent?.match(/^(.+?) uses/)?.[1] || 'Test Service';

      const context = createAnalyticsContext(jwt as any, serviceName, {
        redirectURISuccess:
          'https://webhook.site/bfb3c726-a94b-4c47-89ed-546c92df895a',
        redirectURIError:
          'https://webhook.site/bfb3c726-a94b-4c47-89ed-546c92df895a',
      });

      console.log('[WebhookDebugger] Testing with context:', context);

      // Call the actual function that SelectTSP uses (now calls abandoned endpoint)
      await Analytics.confirmedSkip.tspSelectionQuit(context);

      console.log('[WebhookDebugger] SelectTSP quit test completed');
    } catch (error) {
      console.error('[WebhookDebugger] SelectTSP quit test failed:', error);
    }
  },
};

// Make it available globally for easy debugging
(window as any).WebhookDebugger = WebhookDebugger;

console.log(
  '[WebhookDebugger] Debugging utility loaded. Use WebhookDebugger.printLogs() to see webhook logs.'
);
